<template>
  <div
    ref="editorWrapper"
    class="ts-rich-text"
    :class="{ focus: isFocus, readonly: readonly }"
  >
    <div
      ref="toolbar"
      class="ts-toolbar"
      v-show="isFocus && hasToolbar && !readonly"
    >
      <button class="ql-bold" />
      <button class="ql-italic" />
      <button class="ql-underline" />
      <button class="ql-script" value="sub" />
      <button class="ql-script" value="super" />
      <ColorPicker
        :fontColor="curFontColor"
        :bgColor="curBgColor"
        @reset="resetColors"
        @fontColorChange="fontColorChange"
        @bgColorChange="bgColorChange"
      />
    </div>
    <div ref="editor" :style="`text-indent: ${textIndent}px`"></div>
    <slot />
    <!-- 无坐标错误标记 -->
    <NoCoordError :errors="errorsNoCrood" />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es';
import ColorPicker from './color_picker/Index.vue';
import {
  ERROE_EMBED,
  FONT_DEF_COLOR,
  DIGIT_EMBED,
  ELEMENT_ROLE_SIGN,
  NO_COORD_ERROR_NAME,
  NO_COORD_ERROR_TYPES,
} from './const';
import Quill from './quill/index';
import {
  getQuillText,
  getJsonRemoveExtraMark,
  dotJsonToQuill,
  isDigitEmbedForQuill,
  isErrorEmbed,
  quillJsonToDot,
  isFakeEmbed,
  removeLastNewline
} from './utils';
import FieldEmbed from './quill/emEmbed';
import ImgEmbed from './quill/imgEmbed';
import FindReplaceEmbed from './quill/findReplaceEmbed';
import emitter from './emit';
import './index.scss';
import findReplaceMixin from './mixins/findReplace';
import highlightMixin from './mixins/highlight';
import NoCoordError from './no_coord_error/index.vue';

const outsideDummy = [
  ELEMENT_ROLE_SIGN.errorAdvise,
  ELEMENT_ROLE_SIGN.fieldEmbed,
  ELEMENT_ROLE_SIGN.imgEmbed,
];

const includeElements = ['.find-replace-dialog', '.find-replace-button'];

const classToEmbed = {
  [FieldEmbed.className]: FieldEmbed,
  [ImgEmbed.className]: ImgEmbed,
};

export default {
  components: {
    ColorPicker,
    NoCoordError,
  },
  mixins: [findReplaceMixin, highlightMixin],
  props: {
    jsonText: {
      type: String,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    hasToolbar: {
      type: Boolean,
      default: true,
    },
    pId: {
      type: String || Number,
      default: '',
    },
  },
  data() {
    return {
      isFocus: false,
      quill: null,
      curFontColor: '',
      curBgColor: '',
      errorsWithCoord: [],
      errorsNoCrood: [],
    };
  },
  computed: {
    textIndent() {
      let indent = 2; // 初始右间距
      this.errorsNoCrood?.forEach((item, index) => {
        // 字体长度 + margin长度（index * 4）
        indent +=
          NO_COORD_ERROR_NAME[item.errorType].length * 12 + 8 + index * 4;
      });
      return this.errorsNoCrood?.length ? indent : 0;
    },
  },
  mounted() {
    this.initQuill();
    // 非只读
    if (!this.readonly) {
      this.initEventListener();
      this.buildErrors();
      this.initParseManager();
    }
  },
  watch: {
    errors() {
      this.clearAllErrors();
      this.buildErrors();
    },
    readonly() {
      // 延迟处理，这里readonly变化可能涉及到dom更新，需保证setReadonly操作的dom为最新的
      setTimeout(() => {
        this.readonly && this.setReadonly();
      }, 0);
    },
  },
  methods: {
    // =====================初始化 -> 实例=====================
    initQuill() {
      // 创建quill实例
      this.quill = new Quill(this.$refs.editor, {
        modules: {
          toolbar: {
            container: this.$refs.toolbar,
          },
          keyboard: {
            bindings: {
              undo: {
                key: 'z',
                shortKey: true,
                handler: () => {
                  const modifyItem = (deltaItem) => {
                    // 占位符调整结构
                    if (isDigitEmbedForQuill(deltaItem)) {
                      Object.values(DIGIT_EMBED).forEach((key) => {
                        if (deltaItem.insert?.[key]) {
                          deltaItem.insert = {
                            [key]: deltaItem.attributes,
                          };
                        }
                      });
                    }
                    // 低错不做处理,设置为了source=api生效
                    if (isErrorEmbed(deltaItem)) {
                      deltaItem.attributes = {};
                    }

                    if (isFakeEmbed(deltaItem)) {
                      // 删除查找替换相关的标记
                      delete deltaItem.attributes[FindReplaceEmbed.blotName];
                    }
                  };
                  this.quill.history.stack.undo?.forEach((item) => {
                    item.redo?.forEach((item) => modifyItem(item));
                    item.undo?.forEach((item) => modifyItem(item));
                  });
                  this.quill.history.stack.redo?.forEach((item) => {
                    item.redo?.forEach((item) => modifyItem(item));
                    item.undo?.forEach((item) => modifyItem(item));
                  });

                  this.quill.history.undo();
                },
              },
            },
          },
          history: {
            userOnly: true,
          },
        },
        // 配置白名单blot
        formats: [
          'background',
          'bold',
          'italic',
          'underline',
          'script',
          'color',
          'DotImgEmbed',
          'DotFieldEmbed',
          'DotFontEmbed',
          'LowErrorEmbed',
          'SpellErrorEmbed',
          'CustomErrorEmbed',
          'FindReplaceEmbed',
          'HighlightEmbed',
        ],
        theme: 'snow',
      });

      if (this.jsonText) {
        this.setContents(dotJsonToQuill(JSON.parse(this.jsonText)));
      }

      if (this.readonly) {
        // 注册域的点击插入功能
        this.initFieldTrigger();
        this.setReadonly();
      }
    },
    // 收拢setContents方法，用来处理一些重置数据的逻辑
    setContents(delta, source = 'api') {
      // quill会在行尾没有\n时自动拼一个，这里我们先拼在删，防止其不可控
      delta.push({ insert: '\n' });
      this.quill.setContents(delta, source);
    },
    initFieldTrigger() {
      const digitElements = [
        ...this.$refs.editorWrapper.querySelectorAll(
          `.${FieldEmbed.className}`,
        ),
        ...this.$refs.editorWrapper.querySelectorAll(`.${ImgEmbed.className}`),
      ];
      digitElements.forEach((el) => {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          emitter.emit('digitEmbedClick', {
            node: e.currentTarget,
            targetId: this.pId,
            embed: classToEmbed[e.currentTarget.className],
          });
        });
      });
    },
    addFieldWatcher() {
      emitter.on('digitEmbedClick', ({ node, targetId, embed }) => {
        const { lastInstance, selection, pid } = window.$quillCahce || {};
        // 同行原文域点击添加
        const isSameTarget = targetId === pid;
        if (lastInstance && selection && isSameTarget) {
          const id = node.getAttribute('data-id');
          const isOpen = node.getAttribute('isOpen');
          const isClose = node.getAttribute('isClose');
          let targetIndex = selection.index;
          let oldDigitIndex = 0;
          const hasSameDigit = lastInstance.getContents()?.ops?.some((item) => {
            const {
              id: itemId,
              isOpen: itemIsOpen,
              isClose: itemIsClose,
            } = item?.attributes || {};
            // 坐标计算
            if (typeof item.insert === 'string') {
              oldDigitIndex += item.insert.length;
            } else {
              oldDigitIndex++;
            }

            if (isDigitEmbedForQuill(item) && item?.insert[ImgEmbed.blotName]) {
              return id === itemId;
            }
            // 找到同一个占位符：结束
            return (
              id === itemId &&
              (isOpen === itemIsOpen || isClose === itemIsClose)
            );
          });
          if (hasSameDigit) {
            if (oldDigitIndex <= targetIndex) targetIndex--;
            lastInstance.deleteText(oldDigitIndex - 1, 1, 'user');
          }
          lastInstance.insertEmbed(
            targetIndex,
            embed.blotName,
            {
              id,
              value: node.innerText,
              isOpen,
              isClose,
            },
            'user',
          );
          selection.index = targetIndex + 1;
        }
        // 异行点击也设置光标：优化体验，避免失焦，可继续直接输入
        lastInstance.setSelection(selection?.index, 0, 'api');
      });
    },
    offFieldWatcher() {
      emitter.off('digitEmbedClick');
    },
    setReadonly() {
      this.quill.enable(false);
      this.$refs.editorWrapper
        ?.querySelectorAll('.ql-a-embed-string')
        .forEach((item) => item.setAttribute('contentEditable', false));
    },
    // =====================初始化 -> 粘贴器=====================
    initParseManager() {
      // 过滤掉insert不是string的delta，例如：图片
      this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        delta.ops = delta.ops.filter((item) => {
          if (typeof item.insert === 'string') {
            // 如果粘贴不去掉换行符，会导致复制内容不可控，如：原文预览中选中的句子复制
            item.insert = item.insert.replace(/(<br ?\/?>|\n)/g, '');
            return true;
          }
        });
        return delta;
      });

      // 只能粘贴纯文本
      const plaintextMatcher = (node, delta) => {
        let ops = [];
        delta.ops.forEach((op) => {
          if (op.insert && typeof op.insert === 'string') {
            ops.push({
              insert: op.insert,
            });
          }
        });
        delta.ops = ops;
        return delta;
      }
      this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, plaintextMatcher);
      this.quill.clipboard.addMatcher('B', plaintextMatcher);

    },
    // =====================初始化 -> 事件监听器=====================
    getExposeData() {
      return [
        getQuillText(this.quill),
        getJsonRemoveExtraMark(this.quill),
        removeLastNewline(this.quill.getText()),
      ];
    },
    handleBodyClick(e) {
      const targetRole =
        e.target?.getAttribute('role') ||
        e.target?.parentNode?.getAttribute('role');
      // 兼容处理：其他编辑器的一些元素点击不需要失焦，如：拼写错误建议的点击
      if (outsideDummy.includes(targetRole)) return;

      // 一些特殊的外部元素不需要失焦
      const isInclude = includeElements.some((item) =>
        document.querySelector(item)?.contains(e.target),
      );
      if (isInclude) return;

      // 非编辑器之外的点击
      if (!this.$refs.editorWrapper?.contains(e.target)) {
        /**
         * 处理quill的bug：点击绑定了click事件的外部元素，编辑器不失焦，加了手动的blur
         */
        this.quill.blur();
        this.handleBlur();
      }
    },
    handleBlur() {
      this.offFieldWatcher();
      document.body.removeEventListener('mousedown', this.handleBodyClick);

      this.isFocus = false;
      this.$emit('blur', ...this.getExposeData());
    },
    handleFocus() {
      this.addFieldWatcher();
      // 用mousedown：防止用click导致blur后选区丢失
      document.body.addEventListener('mousedown', this.handleBodyClick);

      this.isFocus = true;
      this.$emit('focus', ...this.getExposeData());
    },
    initEventListener() {
      this.quill.on('selection-change', (range, oldRange, source) => {
        // bugfix: 最后一个字符是域，光标选到末尾无法直接输入，这里手动设置
        if (this.quill.getSelection()?.index === this.quill.getLength() - 1) {
          this.quill.setSelection(this.quill.getSelection()?.index);
        }

        // !this.isFocus: 防止setSelection导致的多次重复触发，如域点击功能
        if (range !== null && oldRange === null && !this.isFocus) {
          this.handleFocus();
        }
      });

      this.quill.on('text-change', (delta, oldDelta, source) => {
        this.quill.formatText(0, 0);
        if (source === 'user') {
          this.$emit('change', ...this.getExposeData());
        }
      });
      // 处理当前光标所在格式
      this.quill.on('editor-change', () => {
        const selection = this.quill.getSelection();
        if (selection) {
          const { color, background } = this.quill.getFormat();
          this.curFontColor = Array.isArray(color)
            ? color[0]
            : color ?? FONT_DEF_COLOR;
          this.curBgColor = Array.isArray(background)
            ? background[0]
            : background ?? '';
        }

        window.$quillCahce = {
          lastInstance: this.quill,
          pid: this.pId,
          selection: selection ?? window.$quillCahce?.selection,
        };
      });
    },
    // =====================低错相关=====================
    buildErrors() {
      this.errorsNoCrood = [];
      this.errorsWithCoord = [];
      this.errors?.forEach((error) => {
        if (NO_COORD_ERROR_TYPES.includes(error.errorType)) {
          // 临时处理：语法错误本期不使用坐标，多种错误只展示一个
          const alreadyHasGrammarError = !!this.errorsNoCrood.find(
            (item) => item.errorType === 'grammar_error',
          );
          if (error.errorType !== 'grammar_error' || !alreadyHasGrammarError) {
             // cloneDeep是因为errorsNoCrood和errorsWithCoord都用了语法错误的对象
            this.errorsNoCrood.push(cloneDeep(error));
          }

          // 语法错误不仅要前面展示标记，还得有低错标识
          if (error.errorType === 'grammar_error') {
            this.errorsWithCoord.push(error);
          }
        } else {
          this.errorsWithCoord.push(error);
        }
      });

      this.buildErrorsWithCoord();
    },
    buildErrorsWithCoord() {
      const textLenght = this.quill.getLength();
      this.errors?.forEach((item) => {
        // 兼容处理：拼写错误/语法错误转为低错显示
        if (item.errorType === 'spell_error') {
          item.errorType = 'low_error';
          item.payload = ['拼写错误'];
        }
        if (item.errorType === 'grammar_error') {
          item.errorType = 'low_error';
        }
        const { position, payload, errorType } = item;
        if (!ERROE_EMBED[errorType]) return;
        // 兼容处理：后端的error坐标有时候会出现问题，导致坐标比原文本还长，这时quill会渲染出多余的换行符号
        if (position[0] > textLenght || position[1] > textLenght) {
          return;
        }
        const range = {
          start: position[0],
          length: position[1] - position[0],
        };
        const text = this.quill.getText(range.start, range.length);
        const formatsCache = this.quill.getContents(range.start, range.length);
        // 兼容：占位符中有低错，则不显示低错
        const hasDitis = formatsCache.ops?.some((item) =>
          isDigitEmbedForQuill(item),
        );
        if (hasDitis) return;
        this.quill.deleteText(range.start, range.length);
        // 插入错误标签
        this.quill.insertEmbed(range.start, ERROE_EMBED[errorType], {
          value: text,
          payload,
          isInsert: true,
          // vue组件传进去，处理交互
          instance: this,
        });
        // 恢复原有的样式
        this.recoverFormat(range, formatsCache);
      });
    },
    recoverFormat(range, formatsCache) {
      let pointer = range.start;
      formatsCache.forEach((item) => {
        const attrs = item.attributes;
        const insert = item.insert ?? '';
        if (attrs) {
          this.quill.formatText(pointer, insert.length, attrs);
        }
        pointer = pointer + insert.length;
      });
    },
    clearAllErrors() {
      // bugfix: 缓存一下history，防止setContents后导致坐标全变了
      const historyCache = cloneDeep(this.quill.history?.stack);
      const filteredContents = getJsonRemoveExtraMark(this.quill);
      this.setContents(dotJsonToQuill(filteredContents));
      this.quill.history.stack = historyCache;
    },
    // 替换所有指定类型标签的文本
    replaceEmbedText(embedName = ERROE_EMBED.custom_error, replaceText) {
      const delta = quillJsonToDot(this.quill.getContents());
      const isTargetEmbed = (embed) =>
        embed && embed.attributes && embed.attributes[embedName];
      const stack = [];
      /**
       * 在对blot中的某一部分添加样式时，quill会把这个标记拆分成多个，比较棘手
       * 处理方式：对delta进行处理，多个同样的目标blot直接合并，沿用第一个的样式，然后进行替换
       */
      while (delta.length) {
        const cur = delta.shift();
        if (isTargetEmbed(cur)) {
          const top = stack[stack.length - 1];
          if (isTargetEmbed(top)) {
            // 存在同样且相邻的blot，合并
            top.insert = top.insert + cur.insert;
          } else {
            stack.push(cur);
          }
        } else {
          stack.push(cur);
        }
      }
      const newDelta = stack.map((item) => {
        if (item.attributes && item.attributes[embedName] !== undefined) {
          item.insert = replaceText;
        }
        return item;
      });
      this.setContents(dotJsonToQuill(newDelta));
      this.$emit('change', ...this.getExposeData());
    },
    // =====================背景色 & 字体颜色=====================
    bgColorChange(color) {
      const selection = this.quill.getSelection(true);
      selection && this.quill.formatText(selection, 'background', color);
      this.$nextTick(() => {
        // 迟于 editor-change 中对color的更改，防止被覆盖，修复bug 3417
        this.curBgColor = color;
      });
      this.$emit('change', ...this.getExposeData());
    },
    fontColorChange(color) {
      const selection = this.quill.getSelection(true);
      selection && this.quill.formatText(selection, 'color', color);
      this.$nextTick(() => {
        // 迟于 editor-change 中对color的更改，防止被覆盖，修复bug 3417
        this.curFontColor = color;
      });
      this.$emit('change', ...this.getExposeData());
    },
    resetColors() {
      const selection = this.quill.getSelection(true);
      if (selection) {
        this.quill.formatText(selection, 'color', '');
        this.quill.formatText(selection, 'background', '');
        this.curBgColor = '';
        this.curFontColor = FONT_DEF_COLOR;
      }
      this.$emit('change', ...this.getExposeData());
    },
    // html转为datel
    htmlToDatel(html) {
      return this.quill.clipboard.convert(html);
    },
  },
};
</script>
